import { useCallback, useMemo, useState } from 'react';

const resolveValue = (row, accessor) => (typeof accessor === 'function' ? accessor(row) : row?.[accessor]);

const isBlank = value => value === null || value === undefined || value === '';

const compareValues = (a, b) =>
    typeof a === 'string' || typeof b === 'string'
        ? String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' })
        : a - b;

const rowComparator = (accessor, dir) => (rowA, rowB) => {
    const a = resolveValue(rowA, accessor);
    const b = resolveValue(rowB, accessor);
    if (isBlank(a) && isBlank(b)) return 0;
    if (isBlank(a)) return 1;
    if (isBlank(b)) return -1;
    return dir === 'asc' ? compareValues(a, b) : compareValues(b, a);
};

const firstClickDirection = (data, accessor) => {
    const sample = data.map(row => resolveValue(row, accessor)).find(value => !isBlank(value));
    return typeof sample === 'string' ? 'asc' : 'desc';
};

const sortGroups = (rows, { key, isParent }, compare) => {
    const groups = new Map();
    rows.forEach(row => {
        const groupKey = key(row);
        if (!groups.has(groupKey)) groups.set(groupKey, { parent: null, children: [] });
        const group = groups.get(groupKey);
        if (!group.parent && isParent(row)) group.parent = row;
        else group.children.push(row);
    });

    const all = [...groups.values()];
    const led = all.filter(group => group.parent).sort((a, b) => compare(a.parent, b.parent));
    const orphaned = all.filter(group => !group.parent);

    return [...led, ...orphaned].flatMap(group => (group.parent ? [group.parent, ...group.children] : group.children));
};

export const useTableSort = (
    data = [],
    { columns = {}, defaultSort = null, pinnedFirst, pinnedLast, groupBy } = {},
) => {
    const [sort, setSort] = useState(defaultSort);

    const onSortChange = useCallback(
        column =>
            setSort(current =>
                current?.column === column
                    ? { column, dir: current.dir === 'asc' ? 'desc' : 'asc' }
                    : { column, dir: firstClickDirection(data, columns[column]) },
            ),
        [data, columns],
    );

    const sortedData = useMemo(() => {
        const accessor = sort && columns[sort.column];
        if (!accessor && !pinnedFirst && !pinnedLast) return data;

        const first = [];
        const last = [];
        const middle = [];
        data.forEach(row => {
            if (pinnedFirst?.(row)) first.push(row);
            else if (pinnedLast?.(row)) last.push(row);
            else middle.push(row);
        });

        if (!accessor) return [...first, ...middle, ...last];

        const compare = rowComparator(accessor, sort.dir);
        const ordered = groupBy ? sortGroups(middle, groupBy, compare) : [...middle].sort(compare);

        return [...first, ...ordered, ...last];
    }, [data, sort, columns, pinnedFirst, pinnedLast, groupBy]);

    return { sort, onSortChange, sortedData };
};
